export const INBOX_CHAT_SIDEBAR_WIDTH = 244
export const INBOX_CHAT_SIDEBAR_MAX_WIDTH = 360
export const INBOX_CHAT_SIDEBAR_WIDTH_STORAGE_KEY = 'trellis-inbox-chat-sidebar-width'

export const INBOX_CHAT_RIGHT_PANEL_WIDTH = 380
export const INBOX_CHAT_RIGHT_PANEL_MIN_WIDTH = 280
export const INBOX_CHAT_RIGHT_PANEL_MAX_WIDTH = 560
export const INBOX_CHAT_RIGHT_PANEL_WIDTH_STORAGE_KEY = 'inbox-v2-chat-right-panel-width'
export const INBOX_CHAT_RIGHT_PANEL_OPEN_STORAGE_KEY = 'inbox-v2-chat-right-panel-open'
export const INBOX_CHAT_RIGHT_PANEL_DESKTOP_QUERY = '(min-width: 1280px)'

export const INBOX_PRIMARY_SIDEBAR_WIDTH = INBOX_CHAT_SIDEBAR_WIDTH
export const INBOX_PRIMARY_SIDEBAR_MIN_WIDTH = INBOX_CHAT_SIDEBAR_WIDTH
export const INBOX_PRIMARY_SIDEBAR_MAX_WIDTH = 420
export const INBOX_PRIMARY_SIDEBAR_WIDTH_STORAGE_KEY = 'trellis-inbox-primary-sidebar-width'

export const INBOX_CHAT_SIDEBAR_TOGGLE_SAFE_SPACE_CLASS = 'w-11'

// Mockup `.composer`: canvas strip with a hairline top; the inner box is a
// surface-1 card with a hairline border on the 10px radius step. The "Reply
// via" row, textarea, and tools row live INSIDE the box.
export const INBOX_CHAT_COMPOSER_FOOTER_CLASS =
  'border-hairline bg-background relative min-w-0 shrink-0 border-t px-3 pt-3 pb-3.5 sm:px-4'
export const INBOX_CHAT_COMPOSER_EDITOR_CLASS =
  'border-hairline bg-card relative rounded-[10px] border'
export const INBOX_CHAT_COMPOSER_REPLY_VIA_ROW_CLASS =
  'border-hairline-faint text-subtitle-2xs text-muted-foreground flex min-w-0 items-center gap-1.5 border-b px-3 py-[7px]'
export const INBOX_CHAT_COMPOSER_TEXTAREA_COLLAPSED_HEIGHT = 40
/**
 * How tall the composer may grow before it starts scrolling.
 *
 * This was 160px — roughly six lines. Past that the field stopped growing and
 * the text being typed scrolled out of sight, which is most of a reply for
 * anything longer than a couple of sentences. The mobile composer had the same
 * problem and now sizes against the screen; this brings web closer in line.
 *
 * Deliberately a fixed value rather than a viewport fraction: this is a module
 * constant read during render and fed into scrollHeight arithmetic, so deriving
 * it from `window` would differ between server and client and break hydration,
 * and would go stale on resize. If a viewport-relative cap is wanted it has to
 * be measured in the component, not here.
 */
export const INBOX_CHAT_COMPOSER_TEXTAREA_MAX_AUTO_HEIGHT = 320
export const INBOX_CHAT_SIDEBAR_COMPOSER_TEXTAREA_MAX_AUTO_HEIGHT = 144

/**
 * Height of the composer when the operator explicitly expands it — a deliberate
 * "give me room to write" surface, not the auto-grow ceiling above.
 */
export const INBOX_CHAT_COMPOSER_TEXTAREA_EXPANDED_HEIGHT = 520
export const INBOX_CHAT_COMPOSER_TEXTAREA_BOX_CLASS = 'h-10 px-3 pt-2 pb-1'
export const INBOX_CHAT_COMPOSER_TEXTAREA_CLASS =
  'text-control md:text-body-sm text-foreground placeholder:text-muted-foreground w-full resize-none overflow-y-auto border-0 bg-transparent focus:ring-0 focus:outline-none disabled:opacity-60'
export const INBOX_CHAT_COMPOSER_TOOLBAR_CLASS =
  'flex min-h-8 min-w-0 items-center gap-0.5 px-2 pb-2'
/** Email composer: subject row between "Reply via" and the rich-text body. */
export const INBOX_CHAT_COMPOSER_EMAIL_SUBJECT_ROW_CLASS = 'flex items-center gap-2 px-4 py-1.5'

// Chat header chrome, shared by <ChatHeader> and the chat loading skeleton so
// the skeleton keeps the loaded header's surface, padding, and title position.
export const INBOX_CHAT_HEADER_CLASS =
  'bg-background/80 relative z-50 flex shrink-0 flex-col backdrop-blur-[10px] after:from-background after:pointer-events-none after:absolute after:inset-x-0 after:top-full after:h-3 after:bg-gradient-to-b after:to-transparent [@media(prefers-reduced-transparency:reduce)]:bg-background [@media(prefers-reduced-transparency:reduce)]:border-hairline [@media(prefers-reduced-transparency:reduce)]:border-b [@media(prefers-reduced-transparency:reduce)]:backdrop-blur-none [@media(prefers-reduced-transparency:reduce)]:after:hidden'
export const INBOX_CHAT_HEADER_MAIN_ROW_CLASS =
  'flex h-12 shrink-0 items-center justify-between gap-1.5 px-2 py-0.5 sm:px-2.5'
// 34px = a 20px title line + a 2px gap + the 12px context line, so the title's
// descenders never touch the phone/property row beneath it. A title without
// context is centered with the 7px offset below.
export const INBOX_CHAT_HEADER_IDENTITY_CLASS = 'relative h-[34px] min-w-0 flex-1 leading-none'
export const INBOX_CHAT_HEADER_TITLE_ROW_CLASS =
  'absolute inset-x-0 top-0 flex h-5 min-w-0 translate-y-[7px] items-center gap-1.5 transition-transform duration-[var(--duration-normal)] ease-[var(--ease-premium)] motion-reduce:transition-none'
// A non-Latin name's Latin reading sits on its own 12px line above the 20px
// title, making a 32px block. The identity grows to 44px so the context line
// keeps its own 12px at the bottom; without context the block centers (6px).
export const INBOX_CHAT_HEADER_IDENTITY_WITH_READING_CLASS = 'h-[44px]'
export const INBOX_CHAT_HEADER_TITLE_ROW_WITH_READING_CLASS =
  'h-8 translate-y-1.5 flex-col items-stretch justify-end gap-0'
export const INBOX_CHAT_HEADER_READING_CLASS =
  'text-body-3xs text-muted-foreground block truncate leading-3 font-normal'
export const INBOX_CHAT_HEADER_TITLE_CLASS =
  'group/header-title text-subtitle-md text-foreground flex max-w-full min-w-0 items-center leading-5 font-medium'
export const INBOX_CHAT_HEADER_ACTIONS_CLASS = 'flex shrink-0 items-center gap-0.5 sm:gap-1'
export const INBOX_CHAT_HEADER_AUTOPILOT_CLASS =
  'border-hairline bg-card text-subtitle-2xs text-muted-foreground inline-flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-2.5 font-medium'

// Timeline geometry, shared by the loaded feed (TimelineFeed + message bubbles)
// and the chat loading skeleton so skeleton bubbles land where real ones will.
export const INBOX_TIMELINE_FEED_BODY_CLASS = 'flex flex-col gap-3.5 px-3 pt-8 pb-5 sm:px-6'
export const INBOX_TIMELINE_FEED_BOTTOM_STACK_CLASS =
  'pointer-events-none sticky bottom-0 z-20 -mx-3 mt-2 -mb-[52px] flex flex-col sm:-mx-6'
export const INBOX_TIMELINE_FEED_BOTTOM_SLOT_CLASS =
  'flex h-10 shrink-0 justify-end px-3 pb-1 sm:px-6'
export const INBOX_TIMELINE_DATE_SEPARATOR_CLASS =
  'pointer-events-none my-1 flex items-center gap-3 text-subtitle-xs text-muted-foreground/70 before:h-px before:flex-1 before:bg-hairline-faint after:h-px after:flex-1 after:bg-hairline-faint'
export const INBOX_TIMELINE_DATE_CHIP_ROW_CLASS =
  'pointer-events-none sticky top-8 z-20 -mb-3.5 flex h-0 translate-y-3 items-center justify-center'
export const INBOX_TIMELINE_BUBBLE_WIDTH_CLASS =
  'w-fit max-w-[min(600px,100%)] min-w-0 overflow-x-hidden'
export const INBOX_TIMELINE_INBOUND_BUBBLE_CLASS =
  'text-body-sm bg-card border-hairline flex flex-col rounded-[12px] rounded-ss-sm border px-[13px] py-[9px]'
export const INBOX_TIMELINE_OUTBOUND_BUBBLE_CLASS =
  'text-body-sm bg-surface-3 flex flex-col rounded-[12px] rounded-se-sm px-[13px] py-[9px]'
export const INBOX_TIMELINE_MESSAGE_TEXT_CLASS =
  'text-body-sm text-foreground text-pretty break-words whitespace-pre-wrap'
export const INBOX_TIMELINE_EMAIL_ROW_CLASS =
  'group hover:bg-muted/30 flex w-full gap-2 rounded-lg transition-colors'
export const INBOX_TIMELINE_EMAIL_COLUMN_CLASS = 'flex min-w-0 flex-1 flex-col gap-1.5'
export const INBOX_TIMELINE_EMAIL_CARD_CLASS =
  'flex w-full max-w-[min(100%,560px)] flex-col gap-3 overflow-x-hidden border p-2.5 2xl:max-w-[min(100%,640px)]'
export const INBOX_TIMELINE_EMAIL_CARD_OUTBOUND_CLASS =
  'rounded-s-xl rounded-b-xl border-[hsl(var(--brand)/0.12)] bg-[hsl(var(--brand)/0.1)] dark:border-[hsl(var(--brand)/0.18)] dark:bg-[hsl(var(--brand)/0.14)]'
export const INBOX_TIMELINE_EMAIL_CARD_INBOUND_CLASS =
  'bg-muted/40 border-border/80 rounded-e-xl rounded-b-xl'
export const INBOX_TIMELINE_EMAIL_HEADER_CLASS = 'relative flex min-w-0 items-start gap-2 pe-8'
export const INBOX_TIMELINE_EMAIL_SUBJECT_CLASS =
  'text-subtitle-xs text-foreground line-clamp-2 font-medium break-words'
export const INBOX_TIMELINE_EMAIL_TEXT_CLASS =
  'text-foreground/90 text-body-xs max-w-full leading-snug [overflow-wrap:anywhere] break-words whitespace-pre-wrap'
export const INBOX_TIMELINE_BUBBLE_STAMP_CLASS =
  'text-subtitle-2xs text-muted-foreground/70 flex items-center gap-1 px-1'

export type InboxSidebarLayoutKind = 'chat' | 'primary'

interface InboxSidebarLayoutConfig {
  maxWidth: number
  minWidth: number
  storageKey: string
  width: number
}

export function clampInboxSidebarWidth(width: number, kind: InboxSidebarLayoutKind) {
  const config = getInboxSidebarLayoutConfig(kind)
  return Math.min(config.maxWidth, Math.max(config.minWidth, width))
}

export function getInboxSidebarLayoutConfig(
  kind: InboxSidebarLayoutKind
): InboxSidebarLayoutConfig {
  if (kind === 'chat') {
    return {
      maxWidth: INBOX_CHAT_SIDEBAR_MAX_WIDTH,
      minWidth: INBOX_CHAT_SIDEBAR_WIDTH,
      storageKey: INBOX_CHAT_SIDEBAR_WIDTH_STORAGE_KEY,
      width: INBOX_CHAT_SIDEBAR_WIDTH,
    }
  }

  return {
    maxWidth: INBOX_PRIMARY_SIDEBAR_MAX_WIDTH,
    minWidth: INBOX_PRIMARY_SIDEBAR_MIN_WIDTH,
    storageKey: INBOX_PRIMARY_SIDEBAR_WIDTH_STORAGE_KEY,
    width: INBOX_PRIMARY_SIDEBAR_WIDTH,
  }
}

export function resolveStoredInboxSidebarWidth(
  kind: InboxSidebarLayoutKind,
  storage: Pick<Storage, 'getItem'> | null | undefined = typeof window === 'undefined'
    ? null
    : window.localStorage
) {
  const config = getInboxSidebarLayoutConfig(kind)

  if (!storage) {
    return config.width
  }

  try {
    const saved = storage.getItem(config.storageKey)
    const savedWidth = saved ? Number.parseInt(saved, 10) : Number.NaN
    return Number.isFinite(savedWidth) ? clampInboxSidebarWidth(savedWidth, kind) : config.width
  } catch {
    return config.width
  }
}

export function clampInboxChatRightPanelWidth(width: number) {
  return Math.min(
    INBOX_CHAT_RIGHT_PANEL_MAX_WIDTH,
    Math.max(INBOX_CHAT_RIGHT_PANEL_MIN_WIDTH, width)
  )
}

export function resolveStoredInboxChatRightPanelWidth(
  storage: Pick<Storage, 'getItem'> | null | undefined = typeof window === 'undefined'
    ? null
    : window.localStorage
) {
  if (!storage) {
    return INBOX_CHAT_RIGHT_PANEL_WIDTH
  }

  try {
    const saved = storage.getItem(INBOX_CHAT_RIGHT_PANEL_WIDTH_STORAGE_KEY)
    const savedWidth = saved ? Number.parseInt(saved, 10) : Number.NaN
    return Number.isFinite(savedWidth)
      ? clampInboxChatRightPanelWidth(savedWidth)
      : INBOX_CHAT_RIGHT_PANEL_WIDTH
  } catch {
    return INBOX_CHAT_RIGHT_PANEL_WIDTH
  }
}

function resolveInboxChatRightPanelDesktopMatch(matchesDesktop: boolean | undefined) {
  if (typeof matchesDesktop === 'boolean') {
    return matchesDesktop
  }
  if (typeof window === 'undefined') {
    return true
  }
  if (typeof window.matchMedia !== 'function') {
    return true
  }
  return window.matchMedia(INBOX_CHAT_RIGHT_PANEL_DESKTOP_QUERY).matches
}

export function resolveStoredInboxChatRightPanelOpen({
  storage = typeof window === 'undefined' ? null : window.localStorage,
  matchesDesktop,
}: {
  storage?: Pick<Storage, 'getItem'> | null | undefined
  // Accepted for call-site compatibility but intentionally NOT used: the
  // reservation panel now coexists with the AI chat (it tiles beside the dock
  // instead of overlapping) and no longer collapses when the chat opens.
  // Only screen width (>= 1280px) + the saved preference decide visibility.
  isAgentOpen?: boolean
  matchesDesktop?: boolean
} = {}) {
  if (!resolveInboxChatRightPanelDesktopMatch(matchesDesktop)) {
    return false
  }

  try {
    const saved = storage?.getItem(INBOX_CHAT_RIGHT_PANEL_OPEN_STORAGE_KEY)
    if (saved === 'true') {
      return true
    }
    if (saved === 'false') {
      return false
    }
  } catch {
    return true
  }

  return true
}
